import AgoraRTC from "agora-rtc-sdk-ng";
import { Buffer } from "buffer";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../api/firebase";
import getAuthHeader from "../../../utils/getAuthHeader";
import { ISlPluginOptions } from ".";

export interface FirebaseData {
  coIntId: string;
  createdAt: Timestamp;
  liveStream: boolean;
  mainId: string;
  switchVideo: boolean;
  switchVideoHand: boolean;
  updatedAt: Timestamp;
}

interface Timestamp {
  nanoseconds: number;
  seconds: number;
}

export interface AgoraRemoteUsers {
  uid: string;
  videoTrack?: any;
  _videoTrack?: any;
}

export class SignLanguageService {
  private uid: string | null;
  private client: any;
  private localVideoTrack: any;
  private remoteUsers: AgoraRemoteUsers[] | [];
  private firebaseData: FirebaseData | {};
  private signLanguageState: any;
  private token: string;
  private resourceId: string;
  private sid: string;
  private agoraUid: string;
  private clientInitialized: boolean;
  private eventTargetAgora: EventTarget;
  private eventTargetFirebase: EventTarget;
  private unsubscribeSnapshot?: () => void; // Variable zum Speichern der Unsubscribe-Funktion

  constructor() {
    this.uid = `${uuidv4()}-Video`;
    this.client = null;
    this.localVideoTrack = null;
    this.remoteUsers = [];
    this.firebaseData = {};
    this.signLanguageState = null;
    this.token = "";
    this.resourceId = "";
    this.sid = "";
    this.agoraUid = "";
    this.clientInitialized = false;
    this.eventTargetAgora = new EventTarget();
    this.eventTargetFirebase = new EventTarget();
  }

  public onRemoteUsersChanged(
    callback: (users: [AgoraRemoteUsers]) => void
  ): void {
    this.eventTargetAgora.addEventListener(
      "remoteUsersUpdated",
      (event: Event) => {
        callback((event as CustomEvent).detail);
      }
    );
  }

  private dispatchRemoteUsersChanged(): void {
    this.eventTargetAgora.dispatchEvent(
      new CustomEvent("remoteUsersUpdated", { detail: this.remoteUsers })
    );
  }

  public onFirebaseChanged(
    callback: (firebaseData: FirebaseData) => void
  ): void {
    this.eventTargetFirebase.addEventListener(
      "firebaseUpdated",
      (event: Event) => {
        callback((event as CustomEvent).detail);
      }
    );
  }

  private dispatchFirebaseChanged(): void {
    this.eventTargetFirebase.dispatchEvent(
      new CustomEvent("firebaseUpdated", { detail: this.firebaseData })
    );
  }

  public async init(options:ISlPluginOptions, clientInstance?: any): Promise<void> {
    if (!clientInstance) {
      clientInstance = AgoraRTC.createClient({ codec: "vp8", mode: "live" });
    }
    AgoraRTC.setLogLevel(4);
    this.client = clientInstance;
    this.clientInitialized = true;
    this.join(options.event.id, "audience");
  }

  public async createLocalTracks(videoConfig?: any): Promise<any[]> {
    const videoTrack = await AgoraRTC.createCameraVideoTrack(videoConfig);
    this.localVideoTrack = videoTrack;
    return [videoTrack];
  }

  public async join(channel: string, role: "audience" | "host"): Promise<void> {
    if (!this.client || !channel || !role) {
      throw new Error("Client, channel, or role is not defined");
    }

    const config = await getAuthHeader();
    const req = {
      data: {
        channelName: channel,
        uid: this.uid,
      },
    };

    const resAgoraToken: any = await axios.get(
      `https://agora-token-service.madarray.solutions/rtcToken?channelName=${channel}`
    );
    this.token = resAgoraToken.data.key;

    await this.client.join(
      "b93de4f7d3ff48689e92b81f2ff0b70a",
      channel,
      this.token,
      this.uid
    );
    this.client.on("user-published", this.handleUserPublished);

    this.client.on("user-unpublished", this.handleUserUnpublished);
    // this.client.on("user-joined", this.handleUserPublished);
    // this.client.on("user-left", this.handleUserPublished);
  }

  // Methode zum Beenden des Snapshots
  public unsubscribeFromFirebase(): void {
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot(); // Unsubscribe den Listener
      this.unsubscribeSnapshot = undefined; // Setze die Variable zurück
    }
  }

  private handleUserPublished = async (user: any, mediaType: any) => {
    await this.client.subscribe(user, mediaType);
    // Füge den User hinzu, falls nicht schon vorhanden
    const existingUser: AgoraRemoteUsers[] = Array.from(
      this.client.remoteUsers
    );
    if (existingUser) {
      this.remoteUsers = existingUser;
      this.dispatchRemoteUsersChanged(); // Event feuern
    }
  };

  private handleUserUnpublished = async (user: any, mediaType: any) => {
    await this.client.subscribe(user, mediaType);
    // UID des Users aus dem Event holen
    const userId = user.uid;

    // Aktualisierte Liste aller Remote-User aus `this.client.remoteUsers`
    const updatedRemoteUsers: any = Array.from(this.client.remoteUsers).filter(
      (u: any) => u.uid !== userId
    );

    this.remoteUsers = updatedRemoteUsers;
    this.dispatchRemoteUsersChanged(); // Event feuern
  };

  public async leave(): Promise<void> {
    if (this.localVideoTrack) {
      this.localVideoTrack.stop();
      this.localVideoTrack.close();
    }
    await this.client?.leave();
    this.remoteUsers = [];

    this.unsubscribeFromFirebase()
    this.firebaseData = {};

  }

  public async initSignFirebase(id: string): Promise<void> {
    const signRef = doc(db, "sign-language-video", id);
    const docSnap = await getDoc(signRef);
    const data = docSnap.data();

    this.unsubscribeFromFirebase();
    // TODO UNSUB KONFIGURIEREN
    this.unsubscribeSnapshot = onSnapshot(signRef, (doc: any) => {
      // IN REDUCER SCHREIBEN!!!
      const data = doc.data();
      this.firebaseData = data;
      this.dispatchFirebaseChanged();
    });
    // await this.join("6798d2a3e957f8f946f8b9a0", "audience")
  }

  public getFirebaseData(): any {
    return this.firebaseData;
  }

  public getSignLanguageState(): any {
    return this.signLanguageState;
  }

  public getRemoteUsers(): any[] {
    return this.remoteUsers;
  }
}
